<template>
  <div ref="rootEl" class="c-overview-header-and-filters-v1" @focusin="onFocusIn" @focusout="onFocusOut">
    <transition name="fade">
      <div v-if="isDimmed" class="c-overview-header-and-filters-v1__overlay fixed inset-0 z-30" aria-hidden="true" @click="onOverlayClick" @touchend.prevent="onOverlayClick" />
    </transition>

    <header class="relative">
      <section class="xl:container xl:mx-auto xl:px-20 2xl:px-64 w-full max-w-full" :class="{ 'relative z-40': isDimmed || isElevated }">
        <div
          class="c-overview-header-and-filters-v1__card bg-white leading-tight flex flex-col items-stretch relative rounded-b-2xl gap-2 lg:gap-[26px] lg:py-5 lg:px-10"
          :class="{ 'is-mobile-active': isDimmed, 'xl:pt-14': isDimmed }"
        >
          <!-- Desktop: fixed positions so only opacity/width animate (no layout jump) -->
          <div class="c-overview-header-and-filters-v1__desktop-top hidden lg:block w-full relative">
            <div
              class="c-overview-header-and-filters-v1__titles-inactive absolute left-0 top-0 lg:pl-28 xl:pl-0 flex flex-col items-start gap-1.5"
              :class="{ 'is-hidden': isDimmed }"
            >
              <h1 class="text-app-primary-1-red--fire-brick font-bold text-5xl leading-[60px]">{{ title }}</h1>
              <p class="text-xl leading-[25px] text-black">{{ introduction }}</p>
            </div>

            <div
              class="c-overview-header-and-filters-v1__titles-active absolute left-0 right-0 top-0 lg:pl-28 lg:pr-16 xl:pl-0 xl:pr-0 flex flex-row items-center justify-between gap-4"
              :class="{ 'is-visible': isDimmed }"
            >
              <h1 class="text-app-primary-1-red--fire-brick font-bold text-[32px] leading-8">{{ t_('active-title') }}</h1>
              <button type="button" class="shrink-0 p-1 text-black hover:text-app-primary-1-red--torch" aria-label="Close" @click="onCloseClick">
                <AtomIconX class="w-[13px] h-[13px]" />
              </button>
            </div>

            <div class="c-overview-header-and-filters-v1__search-wrap absolute right-0 bottom-0" :class="{ 'is-expanded': isDimmed }">
              <input
                ref="desktopSearchInput"
                :value="draftKeyword"
                type="text"
                :placeholder="t_('keyword-placeholder-v1')"
                class="c-overview-header-and-filters-v1__search-input w-full bg-app-primary-grey--black-squeeze rounded-[30px] text-sm italic font-normal border-0"
                :class="{ 'is-keyboard-focused': searchInputKeyboardFocus }"
                role="combobox"
                aria-autocomplete="list"
                :aria-expanded="showSuggestions ? 'true' : 'false'"
                :aria-activedescendant="activeSuggestionId"
                @mousedown="onSearchPointerDown"
                @touchstart="onSearchPointerDown"
                @focus="onSearchFocus"
                @input="onSearchInput"
                @keydown="onSearchKeydown"
                @blur="onSearchBlur"
              />
              <button
                type="button"
                tabindex="-1"
                class="c-overview-header-and-filters-v1__search-icon absolute top-1/2 -translate-y-1/2 p-0 border-0 bg-transparent text-app-primary-1-red--fire-brick cursor-pointer"
                :aria-label="t_('active-title')"
                @click="commitSearch"
              >
                <AtomIconSearch class="w-[14px] h-[14px]" />
              </button>

              <!-- Suggestions dropdown (Elasticsearch-backed, live while typing) -->
              <transition name="fade">
                <ul
                  v-show="showSuggestions"
                  id="overview-search-suggestions-desktop"
                  class="c-overview-header-and-filters-v1__suggestions"
                  role="listbox"
                  @focusout="onSuggestionsFocusOut"
                >
                  <li v-for="(suggestion, index) in suggestions" :key="`${suggestion.type}-${suggestion.label}`" role="presentation">
                    <button
                      :id="`overview-search-suggestion-desktop-${index}`"
                      type="button"
                      role="option"
                      class="c-overview-header-and-filters-v1__suggestion"
                      :class="{ 'is-active': activeSuggestionIndex === index }"
                      :aria-selected="activeSuggestionIndex === index ? 'true' : 'false'"
                      @mousedown.prevent
                      @click="selectSuggestion(suggestion)"
                    >
                      <span class="c-overview-header-and-filters-v1__suggestion-label">{{ suggestion.label }}</span>
                      <span class="c-overview-header-and-filters-v1__suggestion-type">{{ suggestion.type }}</span>
                    </button>
                  </li>
                </ul>
              </transition>
            </div>
          </div>

          <!-- Mobile: animate card/titles; search uses button→input swap so focus stays -->
          <div class="c-overview-header-and-filters-v1__mobile lg:hidden" :class="{ 'is-active': isDimmed }">
            <div class="c-overview-header-and-filters-v1__mobile-header">
              <div class="c-overview-header-and-filters-v1__mobile-titles-inactive">
                <h1 class="text-app-primary-1-red--fire-brick text-2xl leading-[30px] font-bold min-w-0 truncate">{{ title }}</h1>
                <p v-if="introductionMobile" class="text-xs leading-[15px] text-black shrink-0">{{ introductionMobile }}</p>
              </div>
              <div class="c-overview-header-and-filters-v1__mobile-titles-active">
                <h1 class="text-app-primary-1-red--fire-brick text-[32px] leading-8 font-bold shrink-0">{{ t_('active-title') }}</h1>
                <button type="button" class="shrink-0 p-1 text-black hover:text-app-primary-1-red--torch" aria-label="Close" @click="onCloseClick">
                  <AtomIconX class="w-[13px] h-[13px]" />
                </button>
              </div>
            </div>

            <div class="c-overview-header-and-filters-v1__mobile-search c-overview-header-and-filters-v1__search-wrap-mobile">
              <div class="relative w-full">
                <input
                  ref="mobileSearchInput"
                  :value="draftKeyword"
                  type="text"
                  enterkeyhint="search"
                  :placeholder="t_('keyword-placeholder-v1')"
                  class="c-overview-header-and-filters-v1__search-input c-overview-header-and-filters-v1__search-input--mobile w-full bg-app-primary-grey--black-squeeze rounded-[30px] text-xs italic font-normal border-0"
                  :class="{ 'is-mobile-expanded': isDimmed, 'is-keyboard-focused': searchInputKeyboardFocus }"
                  role="combobox"
                  aria-autocomplete="list"
                  :aria-expanded="showSuggestions ? 'true' : 'false'"
                  :aria-activedescendant="activeSuggestionId"
                  @mousedown="onSearchPointerDown"
                  @touchstart="onSearchPointerDown"
                  @focus="onMobileSearchFocus"
                  @input="onSearchInput"
                  @keydown="onSearchKeydown"
                  @blur="onSearchBlur"
                />
                <button
                  type="button"
                  tabindex="-1"
                  class="absolute right-3 top-1/2 -translate-y-1/2 p-0 border-0 bg-transparent text-app-primary-1-red--fire-brick cursor-pointer"
                  :aria-label="t_('active-title')"
                  @click="commitSearch"
                >
                  <AtomIconSearch class="w-3.5 h-3.5" />
                </button>

                <!-- Suggestions dropdown (Elasticsearch-backed, live while typing) -->
                <transition name="fade">
                  <ul
                    v-show="showSuggestions"
                    id="overview-search-suggestions-mobile"
                    class="c-overview-header-and-filters-v1__suggestions c-overview-header-and-filters-v1__suggestions--mobile"
                    role="listbox"
                    @focusout="onSuggestionsFocusOut"
                  >
                    <li v-for="(suggestion, index) in suggestions" :key="`${suggestion.type}-${suggestion.label}`" role="presentation">
                      <button
                        :id="`overview-search-suggestion-mobile-${index}`"
                        type="button"
                        role="option"
                        class="c-overview-header-and-filters-v1__suggestion"
                        :class="{ 'is-active': activeSuggestionIndex === index }"
                        :aria-selected="activeSuggestionIndex === index ? 'true' : 'false'"
                        @mousedown.prevent
                        @click="selectSuggestion(suggestion)"
                      >
                        <span class="c-overview-header-and-filters-v1__suggestion-label">{{ suggestion.label }}</span>
                        <span class="c-overview-header-and-filters-v1__suggestion-type">{{ suggestion.type }}</span>
                      </button>
                    </li>
                  </ul>
                </transition>
              </div>
            </div>

            <div class="c-overview-header-and-filters-v1__mobile-actions">
              <MoleculeButtonSearchFilter
                v-if="showDatePicker"
                class="h-10 w-full flex items-center justify-between px-4 py-3 text-xs font-normal"
                state="neutral"
                @click="toggleMobileDatePicker"
              >
                {{ $t('overview-search-pick-date') }}
                <AtomIconCalendar class="w-4 h-4 text-app-primary-1-red--fire-brick shrink-0" />
              </MoleculeButtonSearchFilter>

              <MoleculeButtonSearchFilter class="h-10 w-full flex items-center justify-between px-4 py-3 text-xs font-normal" state="neutral" @click="toggleMobileFilter">
                {{ $t('overview-search-toggle-filters') }}
                <AtomIconAppFilter class="w-4 h-4 text-app-primary-1-red--fire-brick shrink-0" />
              </MoleculeButtonSearchFilter>
            </div>
          </div>

          <!-- Filters (desktop) -->
          <div class="hidden lg:block">
            <ul class="flex flex-wrap gap-4">
              <li v-if="showDatePicker" class="s-datepicker relative" style="z-index: 30" @mousedown="keepDimmed">
                <!--
                  New datepicker kept for later sprint rollout.
                <MoleculeDatepicker
                  background-color="bg-app-primary-grey--black-squeeze"
                  text-color="text-black hover:text-app-primary-1-red--torch"
                  size="medium"
                  :enable-icon-left="false"
                  :shadowed="false"
                  :icon-size="3"
                  :month-columns="2"
                  :month-rows="1"
                  :month-step="1"
                  class-list="flex h-[30px] px-4 py-0 text-sm text-black rounded-3xl"
                />
                -->
                <MoleculeDatepickerLegacy
                  background-color="bg-app-primary-grey--black-squeeze"
                  text-color="text-black hover:text-app-primary-1-red--torch"
                  size="medium"
                  :enable-icon-left="false"
                  :shadowed="false"
                  :icon-size="3"
                  :month-columns="1"
                  :month-rows="1"
                  :month-step="1"
                  class-list="flex h-[30px] px-4 py-0 text-sm text-black rounded-3xl"
                />
              </li>
              <li v-for="({ values, key }, index) in facets" :key="key" class="relative z-10">
                <MoleculeButton
                  background-color="bg-app-primary-grey--black-squeeze"
                  text-color="text-black hover:text-app-primary-1-red--torch"
                  size="medium"
                  :shadowed="false"
                  :enable-icon-left="false"
                  class="flex ignore-close h-[30px] px-4 py-0 text-sm rounded-3xl"
                  @click="toggleOpen(key)"
                >
                  {{ t_(key) }}
                  <div>
                    <AtomIcon
                      icon="chevron-down"
                      classes="text-app-primary-1-red--fire-brick w-3 h-3 ml-2 transform transition duration-200 shrink-0"
                      :class="{ 'rotate-180': key === currentlyOpen }"
                    />
                  </div>
                </MoleculeButton>

                <div v-if="key === currentlyOpen" v-closable="{ executable: closeAll, exclude: '.ignore-close' }" class="c-filter-dropdown ignore-close">
                  <div class="c-filter-dropdown__content flex flex-col p-6 z-50 bg-white rounded-br-3xl rounded-bl-3xl" :style="{ 'margin-left': `${index * -87}%` }">
                    <div class="flex-auto overflow-auto">
                      <template v-if="values.length <= 8">
                        <ul class="flex flex-row flex-wrap w-full">
                          <li v-for="value in values" :key="value.value" class="mr-4 mb-4">
                            <MoleculeButtonSearchFilter :state="getState(value.active, value.count)" @click="emit('toggleFacet', { name: key, value: value.value })">
                              {{ value.label || t_(value.value) }}
                            </MoleculeButtonSearchFilter>
                          </li>
                        </ul>
                      </template>
                      <template v-else>
                        <ul class="grid lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-3">
                          <li v-for="value in sortBy(values, (value) => value.label || $t(`facet.${value.value}`))" :key="value.value">
                            <AtomLink
                              class="mr-4 mb-2 cursor-pointer hover:text-app-primary-1-red--fire-brick underline font-medium text-sm"
                              :class="{
                                'text-app-primary-1-red--fire-brick font-semibold': value.active,
                                'text-app-primary-grey--concrete pointer-events-none': value.count === 0,
                              }"
                              :fallback-component="value.count !== 0 ? 'button' : 'span'"
                              @click="emit('toggleFacet', { name: key, value: value.value })"
                            >
                              {{ value.label || $t(`facet.${value.value}`) }}
                            </AtomLink>
                          </li>
                        </ul>
                      </template>
                    </div>

                    <div class="flex items-center pt-5 mt-2 border-t-2 border-solid border-app-primary-grey--black-squeeze">
                      <span
                        v-if="dateIsSpecified || hasKeywordSelection || hasActiveRegularFacetSelection"
                        class="underline mr-3 font-semibold text-sm cursor-pointer"
                        @click="emit('reset')"
                      >
                        {{ $t('overview-search-reset') }}
                      </span>
                      <div class="ml-auto font-semibold text-sm cursor-pointer shrink-0 ignore-close" @click="showResultsAndClose">
                        <MoleculeButton :enable-icon-left="false" background-color="bg-app-primary-1-red--fire-brick group-hover:bg-app-primary-1-red--fire-brick" size="medium">
                          {{ $t('overview-search-show-results', totalHits) }}
                        </MoleculeButton>
                      </div>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </div>
        </div>
      </section>

      <section class="xl:container xl:mx-auto px-8 sm:px-10 xl:px-20 2xl:px-64 flex mt-5">
        <!-- Display active filters if any -->
        <div
          v-if="dateIsSpecified || hasKeywordSelection || hasActiveTagsFacetSelection || hasActiveRegularFacetSelection"
          class="text-sm leading-none flex items-center flex-wrap"
        >
          <!-- Active filter with date -->
          <template v-if="dateIsSpecified">
            <p class="font-medium mr-1 mb-1 shrink-0">{{ $t('filters.concerts-from-date', totalHits) }}</p>
            <MoleculeFacet
              class="mb-1 mr-2"
              :title="d_(date, 'short-date')"
              @click="
                () => {
                  removeDateFilter(d_(date, 'short-date'))
                  eventOverviewStore.setDate()
                }
              "
            >
              <AtomIconX class="w-2 h-2 cursor-pointer shrink-0" />
            </MoleculeFacet>
          </template>

          <!-- Active filters with tag -->
          <template v-if="hasActiveTagsFacetSelection">
            <p class="font-medium mr-1 mb-1 shrink-0">{{ $t('filters.active-tags') }}</p>
            <div v-for="{ facet, label, value } in activeTagFacets" :key="value" class="mr-2 mb-1">
              <MoleculeFacet :title="label || t_(value)" @click="emit('toggleFacet', { name: facet, value })">
                <AtomIconX class="w-2 h-2 cursor-pointer shrink-0" />
              </MoleculeFacet>
            </div>
          </template>

          <!-- Active filters with search words -->
          <template v-if="hasKeywordSelection">
            <p class="font-medium mr-1 mb-1 shrink-0">{{ $t('filters.active-search-keywords') }}</p>
            <div class="mr-2 mb-1">
              <MoleculeFacet :title="keyword" @click="emit('setKeyword', '')">
                <AtomIconX class="w-2 h-2 cursor-pointer shrink-0" />
              </MoleculeFacet>
            </div>
          </template>

          <!-- Active filters with facet values -->
          <template v-if="hasActiveRegularFacetSelection">
            <p class="font-medium mr-1 mb-1 shrink-0">{{ $t('filters.active-filters') }}</p>
            <div v-for="{ facet, label, value } in activeRegularFacets" :key="value" class="mr-2 mb-1">
              <MoleculeFacet
                :title="label || t_(value)"
                @click="
                  () => {
                    emit('toggleFacet', { name: facet, value })
                    removeFilter()
                  }
                "
              >
                <AtomIconX class="w-2 h-2 cursor-pointer shrink-0" />
              </MoleculeFacet>
            </div>
          </template>

          <!-- Reset filter link -->
          <p v-if="dateIsSpecified || hasKeywordSelection || hasActiveRegularFacetSelection" class="underline cursor-pointer font-medium mb-1" @click="emit('reset')">
            {{ $t('filters.remove-filters') }}
          </p>
        </div>
      </section>

      <section v-if="mobileFilters" class="fixed bg-app-primary-grey--black-squeeze top-0 right-0 bottom-0 left-0 z-50 lg:hidden overflow-auto pb-20">
        <div class="px-4 pt-10">
          <h5 class="flex items-center">
            <AtomIconAppFilter class="w-6 h-6 text-app-primary-1-red--torch mx-3 shrink-0" />
            <span class="text-app-primary-1-red--fire-brick text-2xl font-semibold">
              {{ $t('overview-search-toggle-filters') }}
            </span>
          </h5>

          <hr class="border-t border-app-primary-1-red--fire-brick mt-4" />

          <ul>
            <li v-for="{ values, key } in facets" :key="key" class="relative border-b border-solid border-app-primary-1-red--fire-brick">
              <span class="flex items-center text-xl text-app-primary-1-red--fire-brick py-6 font-semibold cursor-pointer ignore-close" @click="toggleOpen(key)">
                {{ t_(key) }}
                <AtomIcon
                  icon="chevron-down"
                  classes="text-app-primary-1-red--fire-brick w-4 h-4 ml-2 transform transition duration-200 ml-auto shrink-0"
                  :class="{ 'rotate-180': key === currentlyOpen }"
                />
              </span>

              <template v-if="values.length <= 25">
                <ul v-show="key === currentlyOpen" class="mb-6 ignore-close">
                  <li v-for="value in values" :key="value.value" class="mr-4 mb-4 inline-block">
                    <MoleculeButtonSearchFilter
                      mobile
                      class="ignore-close"
                      :state="getState(value.active, value.count)"
                      :class="{
                        'text-app-primary-1-red--fire-brick': !value.active && value.count !== 0,
                        'text-white font-semibold': value.active,
                        'text-app-primary-grey--concrete pointer-events-none': value.count === 0,
                      }"
                      @click="emit('toggleFacet', { name: key, value: value.value })"
                    >
                      {{ value.label || t_(value.value) }}
                    </MoleculeButtonSearchFilter>
                  </li>
                </ul>
              </template>
              <template v-else>
                <div v-show="key === currentlyOpen" class="mb-6 grid grid-cols-1 sm:grid-cols-2 gap-2 ignore-close">
                  <div
                    v-for="value in sortBy(values, (value) => value.value)"
                    :key="value.value"
                    class="mr-4 mb-1 cursor-pointer hover:underline text-sm ignore-close"
                    :class="{
                      'text-app-primary-1-red--fire-brick font-semibold': value.active,
                      'text-app-primary-grey--concrete pointer-events-none': value.count === 0,
                    }"
                    @click="emit('toggleFacet', { name: key, value: value.value })"
                  >
                    {{ value.label || t_(value.value) }}
                  </div>
                </div>
              </template>
            </li>
          </ul>
        </div>

        <div class="bg-white py-3 rounded-t-3xl z-10 fixed bottom-0 w-full px-5 sm:px-6 shadow-2xl">
          <ol class="flex items-center">
            <li
              v-if="dateIsSpecified || hasKeywordSelection || hasActiveRegularFacetSelection"
              class="flex flex-col items-center hover:text-app-primary-1-red--torch transition duration-300 ease-in-out mr-auto underline font-semibold leading-none cursor-pointer text-sm"
              @click="
                () => {
                  emit('reset')
                  toggleMobileFilter()
                }
              "
            >
              {{ $t('overview-search-reset') }}
            </li>
            <li class="flex flex-col items-center hover:text-app-primary-1-red--torch transition duration-300 ease-in-out ml-auto">
              <MoleculeButtonSearchFilter state="results" @click="toggleMobileFilter">
                {{ $t('overview-search-show-results', totalHits) }}
              </MoleculeButtonSearchFilter>
            </li>
          </ol>
        </div>

        <div class="bg-white top-0 right-0 p-5 absolute rounded-bl-3xl cursor-pointer" @click="toggleMobileFilter">
          <AtomIconX class="w-3 h-3 cursor-pointer shrink-0" />
        </div>
      </section>

      <section v-if="mobileDatePicker" class="fixed bg-app-primary-grey--black-squeeze top-0 right-0 bottom-0 left-0 z-50 lg:hidden overflow-auto">
        <div class="px-4 pt-10">
          <h5 class="flex items-center">
            <AtomIconAppFilter class="w-6 h-6 mx-3 text-app-primary-1-red--torch shrink-0" />
            <span class="text-app-primary-1-red--fire-brick text-2xl">
              {{ $t('overview-search-pick-date') }}
            </span>
          </h5>

          <hr class="border-t border-app-primary-1-red--fire-brick mt-4 mb-6" />

          <!--
            New datepicker kept for later sprint rollout.
          <MoleculeDatepicker
            background-color="bg-app-primary-grey--black-squeeze"
            text-color="black hover:text-app-primary-1-red--torch"
            size="medium"
            :enable-icon-left="false"
            :shadowed="false"
            :icon-size="4"
            class-list="w-full"
            :show-button="false"
            :is-full-width="true"
            :month-columns="1"
            :month-rows="8"
            :month-step="1"
            @setDate="toggleMobileDatePicker"
          />
          -->
          <MoleculeDatepickerLegacy
            background-color="bg-app-primary-grey--black-squeeze"
            text-color="black hover:text-app-primary-1-red--torch"
            size="medium"
            :enable-icon-left="false"
            :shadowed="false"
            :icon-size="4"
            class-list="w-full"
            :show-button="false"
            :is-full-width="true"
            :month-columns="1"
            :month-rows="8"
            :month-step="1"
            @set-date="toggleMobileDatePicker"
          />
          <hr class="border-t border-app-primary-1-red--fire-brick mt-4 mb-6" />
        </div>
        <div class="bg-white top-0 right-0 p-5 absolute rounded-bl-3xl cursor-pointer" @click="toggleMobileDatePicker">
          <AtomIconX class="w-3 h-3 cursor-pointer shrink-0" />
        </div>
      </section>
    </header>
  </div>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'
import sortBy from 'lodash/sortBy'

const OVERLAY_FADE_MS = 250

const props = defineProps({
  activeFacets: { type: Array as PropType<any[]>, required: false, default: () => [] },
  facets: { type: Array as PropType<any[]>, required: false, default: () => [] },
  introductionMobile: { type: String, required: true },
  introduction: { type: String, required: true },
  keyword: { type: String, required: false, default: '' },
  showDatePicker: { type: Boolean, required: false, default: false },
  // When set, live suggestions are fetched from the given Pinia store namespace
  suggestionStoreNamespace: { type: String, required: false, default: '' },
  title: { type: String, required: true },
  totalHits: { type: Number, required: true },
  translationGroup: { type: String, required: true },
})

const emit = defineEmits(['setKeyword', 'toggleFacet', 'reset'])

const eventOverviewStore = useEventOverviewStore()
const mobileMenusStore = useMobileMenusStore()
const registerGtmFilterStart = setupGtmFilterStart()
const registerGtmDateFilterStart = setupGtmDateFilterStart()
const registerGtmFilterMenuClose = setupGtmFilterMenuClose()
const registerGtmFilterRemove = setupGtmFilterRemove()
const registerGtmDateFilterRemove = setupGtmDateFilterRemove()
const registerGtmSuggestionSelect = setupGtmSuggestionSelect()
const registerGtmSearchFocusOpen = setupGtmSearchFocusOpen()
const registerGtmSearchFocusClose = setupGtmSearchFocusClose()
const registerGtmSearchViewAllResults = setupGtmSearchViewAllResults()
const { t_, d_ } = setupTranslationPrefixer('OverviewHeaderAndFilters', props.translationGroup)
// Only the `event-overview` module implements search tracking / suggestions at present; the namespace
// prop is kept for interface compatibility with the Vuex version (mirrors setupGtmViewItemListWatcher).
const currentSearchTrackingSignature = computed(() => eventOverviewStore.searchTrackingSignature)
const lastCompletedSearchTrackingSignature = computed(() => eventOverviewStore.lastCompletedSearchTrackingSignature)
const searchTotalHits = computed(() => eventOverviewStore.totalHits)
const { queueSearchPerformed } = setupGtmSettledSearchPerformed(lastCompletedSearchTrackingSignature, searchTotalHits)
const rootEl = ref<HTMLElement | null>(null)
const desktopSearchInput = ref<HTMLInputElement | null>(null)
const mobileSearchInput = ref<HTMLInputElement | null>(null)
const isDimmed = ref(false)
const isElevated = ref(false)
const currentlyOpen = ref()
// Draft stays local while typing; committed keyword is shown as a pill, not in the input
const draftKeyword = ref('')
watch(
  () => props.keyword,
  (value) => {
    // Only sync when the keyword pill is cleared / reset externally
    if (!value) {
      draftKeyword.value = ''
    }
  },
)
// Only open on typing; hide on blur and keep closed until the user types again
const suggestionsOpen = ref(false)
// Ticket/design: max 10 suggestions on desktop, 8 on mobile
const SUGGESTION_LIMIT_DESKTOP = 10
const SUGGESTION_LIMIT_MOBILE = 8
const suggestionLimit = () => (typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches ? SUGGESTION_LIMIT_MOBILE : SUGGESTION_LIMIT_DESKTOP)

// Debounced fetch dispatched to the store on every keystroke
const fetchSuggestionsDebounced = debounce((q: string) => {
  if (props.suggestionStoreNamespace) {
    eventOverviewStore.fetchKeywordSuggestions({ q, limit: suggestionLimit() })
  }
}, 300)

const suggestionFacetTypes: Record<string, string> = {
  composers: 'Componist',
  discounts: 'Korting',
  genres: 'Genre',
  instruments: 'Instrument',
  rooms: 'Zaal',
  specials: 'Concertreeks',
}

const typeToFacetKey: Record<string, string> = {
  Componist: 'composers',
  Korting: 'discounts',
  Concert: 'concertTitles',
  Genre: 'genres',
  Instrument: 'instruments',
  Zaal: 'rooms',
  Concertreeks: 'specials',
  Organisator: 'organizers',
  Concerttitel: 'concertTitles',
  Musici: 'musicians',
  Werken: 'works',
}

const hiddenSuggestionFacetKeys = new Set(['organizers', 'concertTitles', 'musicians', 'works'])

// Read live suggestions from the store and enrich with current facet values.
const suggestions = computed(() => {
  const query = draftKeyword.value.trim().toLowerCase()
  const apiSuggestions = props.suggestionStoreNamespace ? eventOverviewStore.keywordSuggestions || [] : []
  const limit = suggestionLimit()

  if (!query) {
    return apiSuggestions.slice(0, limit)
  }

  const facetSuggestions = props.facets.flatMap((facetGroup: CmsData) => {
    const facetType = suggestionFacetTypes[facetGroup.key]
    if (!facetType) {
      return []
    }

    return (facetGroup.values || [])
      .filter((entry: CmsData) => entry && entry.count !== 0)
      .map((entry: CmsData) => ({
        label: entry.label || entry.value,
        value: entry.value,
        type: facetType,
        facet: facetGroup.key,
        uri: null as string | null,
        count: entry.count || 0,
        score: entry.count || 0,
        matchRank: 0,
      }))
      .filter((entry: CmsData) => entry.label && entry.label.toLowerCase().includes(query))
  })

  const merged: CmsData[] = [...facetSuggestions, ...apiSuggestions]
  const deduped: CmsData[] = []
  const seen = new Set<string>()

  merged.forEach((entry) => {
    const key = `${entry.type}|${(entry.value || entry.label || '').toLowerCase()}`
    if (!seen.has(key)) {
      seen.add(key)
      deduped.push(entry)
    }
  })

  return deduped.slice(0, limit)
})
const showSuggestions = computed(() => suggestionsOpen.value && suggestions.value.length > 0)

// Text inputs match :focus-visible on click too; only show a ring after Tab/keyboard focus
const searchInputKeyboardFocus = ref(false)
let focusCameFromKeyboard = false
// Arrow Up/Down highlight while focus stays in the search input
const activeSuggestionIndex = ref(-1)

const onFocusModalityKeyDown = (event: KeyboardEvent) => {
  if (event.key === 'Tab') {
    focusCameFromKeyboard = true
  }
}

const onFocusModalityPointerDown = () => {
  focusCameFromKeyboard = false
  searchInputKeyboardFocus.value = false
}

const onSearchFocus = () => {
  // Only set true on keyboard; do not clear on programmatic re-focus after Tab
  if (focusCameFromKeyboard) {
    searchInputKeyboardFocus.value = true
  }
  focusCameFromKeyboard = false
}

const onSearchInput = (event: Event) => {
  const value = (event.target as HTMLInputElement).value
  draftKeyword.value = value
  const trimmed = value.trim()
  suggestionsOpen.value = trimmed.length > 0
  activeSuggestionIndex.value = -1
  if (props.suggestionStoreNamespace) {
    if (trimmed) {
      fetchSuggestionsDebounced(trimmed)
    } else {
      fetchSuggestionsDebounced.cancel()
      eventOverviewStore.setKeywordSuggestions([])
    }
  }
}

const onSearchBlur = () => {
  debugLog('onSearchBlur')
  // Delay so a tap on a suggestion can register before the list hides (mobile),
  // and so a brief re-focus during the open animation keeps the Tab ring.
  setTimeout(() => {
    const active = document.activeElement
    if (active !== desktopSearchInput.value && active !== mobileSearchInput.value) {
      searchInputKeyboardFocus.value = false
      activeSuggestionIndex.value = -1
    }
    if (active instanceof Element && active.closest('.c-overview-header-and-filters-v1__suggestions')) {
      return
    }
    suggestionsOpen.value = false
  }, 150)
}

// Close the list when Tab leaves the suggestions (unless focus returns to the search input)
const onSuggestionsFocusOut = () => {
  setTimeout(() => {
    const active = document.activeElement
    if (active instanceof Element && active.closest('.c-overview-header-and-filters-v1__suggestions')) {
      return
    }
    if (active === desktopSearchInput.value || active === mobileSearchInput.value) {
      return
    }
    suggestionsOpen.value = false
  }, 0)
}

let clearDimTimeout: ReturnType<typeof setTimeout> | null = null
let elevateTimeout: ReturnType<typeof setTimeout> | null = null
// Skip overlay click / focusout for the gesture that opens the active state
let ignoreOverlayClickUntil = 0
let ignoreFocusOutUntil = 0
// After submit/suggestion, block reopen while the same pointer gesture finishes
let ignoreFocusInUntil = 0
// Guard the opening pointer gesture so the matching mouseup/click cannot immediately close via overlay.
let openingPointerGestureActive = false

/*
 * Opt-in tracing for the dim/elevate/suggestion state machine: set `window.__overviewHeaderDebug = true`
 * from the browser console to have the handlers below log every transition. Never set by the app itself.
 */
declare global {
  interface Window {
    __overviewHeaderDebug?: boolean
  }
}

// Event targets are typed as the bare `EventTarget`; narrow to an Element before reading DOM properties.
const asElement = (target: EventTarget | null | undefined) => (target instanceof Element ? target : null)

const debugEnabled = () => import.meta.client && window.__overviewHeaderDebug === true
const debugLog = (eventName: string, details: CmsData = {}) => {
  if (!debugEnabled()) {
    return
  }

  const payload = {
    isDimmed: isDimmed.value,
    isElevated: isElevated.value,
    currentlyOpen: currentlyOpen.value,
    openingPointerGestureActive,
    ignoreFocusInUntil,
    ignoreFocusOutUntil,
    ignoreOverlayClickUntil,
    now: Date.now(),
    ...details,
  }

  console.groupCollapsed(`[OverviewHeaderV1] ${eventName}`)
  console.table(payload)
  console.groupEnd()
}

const cancelClearDimmed = () => {
  if (clearDimTimeout) {
    clearTimeout(clearDimTimeout)
    clearDimTimeout = null
  }
}

const cancelElevateTimeout = () => {
  if (elevateTimeout) {
    clearTimeout(elevateTimeout)
    elevateTimeout = null
  }
}

const isMobileViewport = () => typeof window !== 'undefined' && window.matchMedia('(max-width: 1023px)').matches

const activeSuggestionId = computed(() => {
  if (activeSuggestionIndex.value < 0 || !showSuggestions.value) {
    return undefined
  }
  const prefix = isMobileViewport() ? 'mobile' : 'desktop'
  return `overview-search-suggestion-${prefix}-${activeSuggestionIndex.value}`
})

const setOverviewHeaderActive = (active: boolean) => {
  // Home/lyre chrome only applies on mobile; keep desktop header intact
  mobileMenusStore.setOverviewHeaderActive(active && isMobileViewport())
}

const blurSearchInputs = () => {
  desktopSearchInput.value?.blur?.()
  mobileSearchInput.value?.blur?.()
  if (document.activeElement instanceof HTMLElement && rootEl.value?.contains(document.activeElement)) {
    document.activeElement.blur()
  }
}

const onSearchPointerDown = () => {
  openingPointerGestureActive = true
  debugLog('onSearchPointerDown:start')

  const clearPointerGuard = () => {
    // Delay one tick so the click event produced by this pointer-up is also ignored.
    setTimeout(() => {
      openingPointerGestureActive = false
      debugLog('onSearchPointerDown:end')
    }, 0)
    window.removeEventListener('mouseup', clearPointerGuard, true)
    window.removeEventListener('touchend', clearPointerGuard, true)
    window.removeEventListener('touchcancel', clearPointerGuard, true)
  }

  window.addEventListener('mouseup', clearPointerGuard, true)
  window.addEventListener('touchend', clearPointerGuard, true)
  window.addEventListener('touchcancel', clearPointerGuard, true)
}

const keepDimmed = () => {
  debugLog('keepDimmed:before')
  if (Date.now() < ignoreFocusInUntil) {
    debugLog('keepDimmed:blockedByIgnoreFocusIn')
    return
  }
  cancelClearDimmed()
  // Any interaction inside the open header should keep the current gesture protected,
  // otherwise the mouseup blur can collapse the active state immediately.
  if (isMobileViewport()) {
    ignoreOverlayClickUntil = Date.now() + 1200
    ignoreFocusOutUntil = Date.now() + 1200
  } else {
    ignoreOverlayClickUntil = Date.now() + 500
    ignoreFocusOutUntil = Date.now() + 500
  }
  if (!isDimmed.value) {
    // Opening: elevate + hide Home/lyre in the same update as the active UI
    isElevated.value = true
    setOverviewHeaderActive(true)
  }
  isDimmed.value = true
  debugLog('keepDimmed:after')
}

const isCalendarOpen = () => Boolean(document.querySelector('.vc-popover-content'))

const scheduleClearDimmed = () => {
  debugLog('scheduleClearDimmed:scheduled')
  cancelClearDimmed()
  clearDimTimeout = setTimeout(() => {
    const focusStillInside = rootEl.value && rootEl.value.contains(document.activeElement)
    // V-Calendar popover renders outside this component; keep active while it is open
    if (!focusStillInside && !currentlyOpen.value && !isCalendarOpen()) {
      isDimmed.value = false
      debugLog('scheduleClearDimmed:cleared', { focusStillInside })
    } else {
      debugLog('scheduleClearDimmed:keptOpen', { focusStillInside })
    }
    clearDimTimeout = null
  }, 50)
}

// Track the reason the overlay is being dismissed so search_focus_close can include close_method.
let pendingCloseMethod: string | null = null

watch(isDimmed, (dimmed) => {
  debugLog('watch:isDimmed', { dimmed })
  cancelElevateTimeout()
  setOverviewHeaderActive(dimmed)
  if (dimmed) {
    isElevated.value = true
    registerGtmSearchFocusOpen()
  } else {
    elevateTimeout = setTimeout(() => {
      if (!isDimmed.value) {
        isElevated.value = false
      }
      elevateTimeout = null
    }, OVERLAY_FADE_MS)
  }
})

const deactivate = () => {
  debugLog('deactivate:before')
  if (isDimmed.value) {
    // Fire search_focus_close with the tracked close_method (if any) before clearing state.
    const method = pendingCloseMethod
    pendingCloseMethod = null
    registerGtmSearchFocusClose(method || undefined)
  }
  pendingCloseMethod = null
  cancelClearDimmed()
  currentlyOpen.value = undefined
  isDimmed.value = false
  blurSearchInputs()
  debugLog('deactivate:after')
}

const leaveSearchFocus = () => {
  ignoreFocusInUntil = Date.now() + 800
  ignoreFocusOutUntil = Date.now() + 800
  ignoreOverlayClickUntil = Date.now() + 800
  deactivate()
  nextTick(blurSearchInputs)
  setTimeout(blurSearchInputs, 0)
}

const commitSearch = () => {
  suggestionsOpen.value = false
  activeSuggestionIndex.value = -1
  const value = draftKeyword.value
  if (value !== props.keyword) {
    emit('setKeyword', value)
  }
  registerGtmSearchViewAllResults()
  queueSearchPerformed(currentSearchTrackingSignature.value)
  // Clear the field; the active keyword pill is the source of truth after submit
  draftKeyword.value = ''
  if (props.suggestionStoreNamespace) {
    eventOverviewStore.setKeywordSuggestions([])
  }
  leaveSearchFocus()
}

const showResultsAndClose = () => {
  currentlyOpen.value = undefined
  deactivate()
}

const selectSuggestion = (suggestion: CmsData) => {
  suggestionsOpen.value = false
  activeSuggestionIndex.value = -1

  const facetKey = suggestion.facet || typeToFacetKey[suggestion.type]
  const suggestionValue = suggestion.value || suggestion.label

  // Fire suggestion_select with the facet key and server-side matchRank.
  registerGtmSuggestionSelect({
    suggestionType: facetKey || suggestion.type,
    matchRank: suggestion.matchRank ?? 0,
  })

  if (facetKey && hiddenSuggestionFacetKeys.has(facetKey) && suggestionValue) {
    // Clear existing keyword first so hidden filters do not keep stale free-text search context.
    if (props.keyword) {
      emit('setKeyword', '')
    }

    setGtmFilterSelectClickLocation('Suggestion')
    emit('toggleFacet', { name: facetKey, value: suggestionValue })
    queueSearchPerformed(currentSearchTrackingSignature.value)
    draftKeyword.value = ''
    leaveSearchFocus()
    return
  }

  if (facetKey) {
    const facetGroup = props.facets.find((group: CmsData) => group.key === facetKey)
    if (facetGroup) {
      const match = facetGroup.values?.find((entry: CmsData) => {
        const entryLabel = (entry.label || entry.value || '').toLowerCase()
        const entryValue = (entry.value || '').toLowerCase()
        const wantedLabel = suggestion.label.toLowerCase()
        const wantedValue = (suggestion.value || suggestion.label).toLowerCase()
        return entryLabel === wantedLabel || entryValue === wantedValue
      })
      const value = match?.value || suggestionValue

      // Clear an existing keyword first so facet fetch does not run with stale keyword context.
      if (props.keyword) {
        emit('setKeyword', '')
      }

      setGtmFilterSelectClickLocation('Suggestion')
      emit('toggleFacet', { name: facetKey, value })
      queueSearchPerformed(currentSearchTrackingSignature.value)
      draftKeyword.value = ''
      leaveSearchFocus()
      return
    }
  }

  // Non-filter types: commit as keyword search, then clear the input (pill remains)
  emit('setKeyword', suggestion.label)
  queueSearchPerformed(currentSearchTrackingSignature.value)
  draftKeyword.value = ''
  leaveSearchFocus()
}

const onSearchKeydown = (event: KeyboardEvent) => {
  const count = suggestions.value.length
  const listOpen = showSuggestions.value && count > 0

  if (event.key === 'ArrowDown' && listOpen) {
    event.preventDefault()
    activeSuggestionIndex.value = activeSuggestionIndex.value < count - 1 ? activeSuggestionIndex.value + 1 : 0
    return
  }

  if (event.key === 'ArrowUp' && listOpen) {
    event.preventDefault()
    activeSuggestionIndex.value = activeSuggestionIndex.value <= 0 ? count - 1 : activeSuggestionIndex.value - 1
    return
  }

  if (event.key === 'Enter') {
    event.preventDefault()
    if (listOpen && activeSuggestionIndex.value >= 0) {
      selectSuggestion(suggestions.value[activeSuggestionIndex.value])
      return
    }
    commitSearch()
  }
}

const closeAll = (event?: Event) => {
  debugLog('closeAll', {
    targetClass: asElement(event?.target)?.className,
    targetTag: asElement(event?.target)?.tagName,
  })

  const target = event?.target
  if (!(target instanceof Element)) {
    debugLog('closeAll:ignoredInvalidTarget')
    return
  }

  // Ignore synthetic/duplicate close events while clicking inside the component.
  if (rootEl.value?.contains(target) && !currentlyOpen.value) {
    debugLog('closeAll:ignoredInsideRootWithoutOpenDropdown')
    keepDimmed()
    return
  }

  currentlyOpen.value = undefined
  // Keep active state when moving to search or the date picker
  if (target.closest('.c-overview-header-and-filters-v1__search-wrap, .c-overview-header-and-filters-v1__search-wrap-mobile, .s-datepicker')) {
    debugLog('closeAll:keptOpenForSearchOrDatepicker')
    keepDimmed()
    return
  }
  /*
   * Mobile: activating search reorders flex items under the same tap, so the
   * trailing click lands on titles/header instead of the input. v-closable then
   * treats that as outside and would deactivate; keep the active state instead.
   * Exception: the Close (X) control, whose click must be allowed to deactivate
   * (closable runs on the same click and would otherwise call keepDimmed again).
   */
  if (isMobileViewport() && target.closest('.c-overview-header-and-filters-v1__mobile')) {
    if (target.closest('[aria-label="Close"]')) {
      debugLog('closeAll:mobileCloseButton')
      return
    }
    debugLog('closeAll:keptOpenForMobile')
    keepDimmed()
    return
  }

  debugLog('closeAll:deactivate')
  deactivate()
}

const toggleOpen = (facet?: string) => {
  const next = facet === currentlyOpen.value ? undefined : facet
  currentlyOpen.value = next
  if (next) {
    keepDimmed()
  }
}

// Single persistent input: a normal tap/focus opens the active state
const onMobileSearchFocus = () => {
  onSearchFocus()
  if (Date.now() < ignoreFocusInUntil) {
    mobileSearchInput.value?.blur?.()
    return
  }
  ignoreFocusOutUntil = Date.now() + 1200
  ignoreOverlayClickUntil = Date.now() + 1200
  keepDimmed()
  // Layout shift (Home/lyre hide, padding) can steal focus mid-tap; put it back
  requestAnimationFrame(() => {
    if (Date.now() < ignoreFocusInUntil) {
      return
    }
    const input = mobileSearchInput.value
    if (isDimmed.value && input && document.activeElement !== input && typeof input.focus === 'function') {
      input.focus({ preventScroll: true })
    }
  })
}

const onFocusIn = () => {
  debugLog('onFocusIn')
  if (Date.now() < ignoreFocusInUntil) {
    debugLog('onFocusIn:blockedByIgnoreFocusIn')
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur()
    }
    return
  }
  keepDimmed()
}

const onFocusOut = (event: FocusEvent) => {
  const relatedTarget = asElement(event.relatedTarget)
  debugLog('onFocusOut', {
    relatedTargetClass: relatedTarget?.className,
    relatedTargetTag: relatedTarget?.tagName,
  })
  if (Date.now() < ignoreFocusOutUntil) {
    debugLog('onFocusOut:blockedByIgnoreFocusOut')
    return
  }
  // Mobile: focusout is unreliable (layout shift + keyboard). Close only via overlay/X.
  if (isMobileViewport()) {
    debugLog('onFocusOut:ignoredOnMobile')
    return
  }
  // Focus is moving to another control inside this component: keep the overlay mounted
  if (relatedTarget && rootEl.value && rootEl.value.contains(relatedTarget)) {
    debugLog('onFocusOut:focusStillInside')
    return
  }
  // Focus moved into the V-Calendar popover (teleported outside the component)
  if (relatedTarget?.closest?.('.vc-popover-content, .vc-container')) {
    debugLog('onFocusOut:calendarTarget')
    return
  }
  scheduleClearDimmed()
}

const onOverlayClick = (event?: Event) => {
  debugLog('onOverlayClick', { targetClass: asElement(event?.target)?.className })
  if (openingPointerGestureActive) {
    debugLog('onOverlayClick:blockedByPointerGuard')
    event?.preventDefault?.()
    event?.stopPropagation?.()
    return
  }
  if (Date.now() < ignoreOverlayClickUntil) {
    debugLog('onOverlayClick:blockedByIgnoreOverlayClickUntil')
    event?.preventDefault?.()
    event?.stopPropagation?.()
    return
  }
  pendingCloseMethod = 'click_outside'
  deactivate()
}

// Explicit close control: always deactivate (ignore window is only for the opening gesture)
const onCloseClick = () => {
  ignoreOverlayClickUntil = 0
  ignoreFocusOutUntil = 0
  pendingCloseMethod = 'cross_icon'
  deactivate()
}

const onEscapeKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape' && isDimmed.value) {
    event.preventDefault()
    pendingCloseMethod = 'escape'
    deactivate()
  }
}

onMounted(() => {
  window.addEventListener('keydown', onEscapeKey)
  window.addEventListener('keydown', onFocusModalityKeyDown, true)
  window.addEventListener('mousedown', onFocusModalityPointerDown, true)
  window.addEventListener('touchstart', onFocusModalityPointerDown, true)

  // When navigating from the homepage search, fire search_performed once the search settles.
  if (eventOverviewStore.pendingSearchPerformed) {
    eventOverviewStore.setPendingSearchPerformed(false)
    queueSearchPerformed(currentSearchTrackingSignature.value)
  }
})

onBeforeUnmount(() => {
  cancelClearDimmed()
  cancelElevateTimeout()
  fetchSuggestionsDebounced.cancel()
  mobileMenusStore.setOverviewHeaderActive(false)
  window.removeEventListener('keydown', onEscapeKey)
  window.removeEventListener('keydown', onFocusModalityKeyDown, true)
  window.removeEventListener('mousedown', onFocusModalityPointerDown, true)
  window.removeEventListener('touchstart', onFocusModalityPointerDown, true)
})

const getState = (active: boolean, count: number) => {
  if (active) {
    return 'active'
  }

  if (count === 0) {
    return 'unavailable'
  }
}
const mobileFilters = ref(false)
const mobileDatePicker = ref(false)
const toggleMobileFilter = () => {
  mobileMenusStore.toggleMobileFilter()
  mobileFilters.value = mobileMenusStore.mobileFilterStatus

  if (mobileMenusStore.mobileFilterStatus === true) {
    registerGtmFilterStart()
  } else {
    registerGtmFilterMenuClose()
  }
}
const toggleMobileDatePicker = () => {
  mobileMenusStore.toggleMobileFilter()
  mobileDatePicker.value = mobileMenusStore.mobileFilterStatus
  if (mobileDatePicker.value === true) {
    registerGtmDateFilterStart()
  }
}

const removeFilter = () => {
  registerGtmFilterRemove()
}

const removeDateFilter = (formattedDate: string) => {
  registerGtmDateFilterRemove(formattedDate)
}

const activeTagFacets = computed(() => props.activeFacets.filter(({ facet }) => facet === 'tags'))
const activeRegularFacets = computed(() => props.activeFacets.filter(({ facet }) => facet !== 'tags'))

const hasActiveTagsFacetSelection = computed(() => activeTagFacets.value.length > 0)
const hasKeywordSelection = computed(() => props.keyword !== '')
const hasActiveRegularFacetSelection = computed(() => activeRegularFacets.value.length > 0)

const date = computed(() => eventOverviewStore.date)
const dateIsSpecified = computed(() => eventOverviewStore.dateIsSpecified)
</script>

<style lang="scss" scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
}

.c-overview-header-and-filters-v1 {
  width: 100%;
  max-width: 100%;
}

.c-overview-header-and-filters-v1__overlay {
  background-color: rgba(0, 0, 0, 0.3);
}

/* Sit above the site header Home/lyre (header is z-50) so the full bar is tappable */
.c-overview-header-and-filters-v1__search-wrap-mobile {
  position: relative;
  z-index: 60;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
}

$mobile-expand-duration: 0.35s;
$mobile-expand-ease: cubic-bezier(0.4, 0, 0.2, 1);

.c-overview-header-and-filters-v1__card {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  padding-bottom: 15px;
  box-shadow:
    0px 10px 15px rgba(0, 0, 0, 0.1),
    0px 4px 6px rgba(0, 0, 0, 0.1);
  transition: padding $mobile-expand-duration $mobile-expand-ease;

  &.is-mobile-active {
    padding-top: 32px;
    padding-bottom: 16px;
  }

  @screen lg {
    height: 189px;
    padding-top: 1.25rem;
    padding-bottom: 1.25rem;
    padding-left: 2.5rem;
    padding-right: 2.5rem;
    box-shadow:
      0px 10px 15px -3px rgba(0, 0, 0, 0.1),
      0px 4px 6px -4px rgba(0, 0, 0, 0.1);
    transition: none;

    &.is-mobile-active {
      padding-top: 1.25rem;
      padding-bottom: 1.25rem;
    }
  }
}

.c-overview-header-and-filters-v1__mobile {
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-width: 0;
  transition:
    gap $mobile-expand-duration $mobile-expand-ease,
    padding $mobile-expand-duration $mobile-expand-ease;

  &.is-active {
    gap: 16px;
    padding-left: 16px;
    padding-right: 16px;
  }

  @screen lg {
    display: none;
  }
}

.c-overview-header-and-filters-v1__mobile-header {
  position: relative;
  min-height: 30px;
  order: 2;
  padding: 0 16px;
  min-width: 0;
  transition:
    padding $mobile-expand-duration $mobile-expand-ease,
    min-height $mobile-expand-duration $mobile-expand-ease;

  .is-active & {
    order: 1;
    min-height: 42px;
    padding: 12px 48px 0 0;
  }
}

.c-overview-header-and-filters-v1__mobile-titles-inactive {
  display: flex;
  align-items: center;
  gap: 10px;
  min-height: 30px;
  min-width: 0;
  transition: opacity $mobile-expand-duration ease;

  .is-active & {
    position: absolute;
    left: 0;
    right: 0;
    top: 0;
    opacity: 0;
    pointer-events: none;
  }
}

.c-overview-header-and-filters-v1__mobile-titles-active {
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-height: 32px;
  min-width: 0;
  opacity: 0;
  pointer-events: none;
  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  transition: opacity $mobile-expand-duration ease;

  .is-active & {
    position: relative;
    opacity: 1;
    pointer-events: auto;
  }
}

.c-overview-header-and-filters-v1__mobile-search {
  order: 1;
  margin-top: 12px;
  // Room for Home (left) and lyre (right); scale down on very narrow viewports
  padding-left: 120px;
  padding-right: 48px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  position: relative;
  z-index: 20;
  transition:
    padding $mobile-expand-duration $mobile-expand-ease,
    margin $mobile-expand-duration $mobile-expand-ease;

  .is-active & {
    order: 2;
    margin-top: 0;
    padding-left: 0;
    padding-right: 0;
  }

  @media (max-width: 359px) {
    padding-left: 72px;
    padding-right: 36px;
  }
}

.c-overview-header-and-filters-v1__mobile-actions {
  order: 3;
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 16px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  box-sizing: border-box;
  transition:
    gap $mobile-expand-duration $mobile-expand-ease,
    padding $mobile-expand-duration $mobile-expand-ease;

  .is-active & {
    gap: 16px;
    padding: 0;
  }
}

.c-overview-header-and-filters-v1__desktop-top {
  height: 93px;
  flex-shrink: 0;
}

.c-overview-header-and-filters-v1__titles-inactive,
.c-overview-header-and-filters-v1__titles-active {
  transition: opacity 0.25s ease;
}

.c-overview-header-and-filters-v1__titles-inactive {
  opacity: 1;
  pointer-events: auto;

  &.is-hidden {
    opacity: 0;
    pointer-events: none;
  }
}

.c-overview-header-and-filters-v1__titles-active {
  opacity: 0;
  pointer-events: none;
  height: 32px;

  &.is-visible {
    opacity: 1;
    pointer-events: auto;
  }
}

.c-overview-header-and-filters-v1__search-wrap {
  width: 290px;
  height: 45px;
  display: flex;
  align-items: center;
  transition: width 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  &.is-expanded {
    width: 100%;
  }
}

.c-overview-header-and-filters-v1__suggestions {
  @apply absolute left-0 right-0 flex flex-col bg-white;
  top: calc(100% + 12px);
  z-index: 50;
  padding: 0;
  gap: 0;
  border: 0.5px solid rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  box-shadow:
    0px 10px 15px -3px rgba(0, 0, 0, 0.1),
    0px 4px 6px -4px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  &--mobile {
    z-index: 70;
  }
}

.c-overview-header-and-filters-v1__suggestion {
  @apply flex w-full items-center justify-between gap-2 text-left;
  padding: 16px 32px;
  color: #303030;
  font-family: FuturaTL, Helvetica, Arial, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0;
  vertical-align: middle;

  .c-overview-header-and-filters-v1__suggestions--mobile & {
    padding: 12px 16px;
  }

  &:hover,
  &:focus,
  &.is-active {
    outline: none;
    background-color: theme('colors.app.primary-grey--black-squeeze');

    .c-overview-header-and-filters-v1__suggestion-label {
      color: theme('colors.app.primary-1-red--fire-brick');
      font-weight: 600;
    }
  }
}

.c-overview-header-and-filters-v1__suggestion-label {
  @apply min-w-0;
  color: rgba(48, 48, 48, 1);
  font-family: FuturaTL, Helvetica, Arial, sans-serif;
  font-weight: 500;
  font-style: normal;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0;
  vertical-align: middle;
}

.c-overview-header-and-filters-v1__suggestion-type {
  @apply shrink-0 text-right;
  color: rgba(86, 70, 72, 1);
  font-family: FuturaTL, Helvetica, Arial, sans-serif;
  font-weight: 600;
  font-style: italic;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0;
  vertical-align: middle;
}

.c-overview-header-and-filters-v1__search-input {
  height: 32px;
  padding: 8px 16px;
  color: #303030;
  font-weight: 400;
  background-color: theme('colors.app.primary-grey--black-squeeze');
  outline: none;
  transition:
    height 0.35s cubic-bezier(0.4, 0, 0.2, 1),
    padding 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  // Keyboard-only: restore the browser default focus outline (same as other elements).
  // -webkit-focus-ring-color does not work as a box-shadow color, so use outline: auto.
  &.is-keyboard-focused {
    outline: 5px auto -webkit-focus-ring-color;
    outline-offset: 0;
  }

  .is-expanded & {
    height: 45px;
    padding: 8px 24px;
  }

  // Vendor placeholders must be separate rules: a browser drops the whole group if it
  // does not recognize one selector, which leaves the default semi-transparent placeholder.
  &::placeholder {
    opacity: 1;
    font-style: italic;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: #303030;
    font-weight: 400;
  }

  &::-webkit-input-placeholder {
    opacity: 1;
    font-style: italic;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: #303030;
    font-weight: 400;
  }

  &::-moz-placeholder {
    opacity: 1;
    font-style: italic;
    font-size: 0.875rem;
    line-height: 1.25rem;
    color: #303030;
    font-weight: 400;
  }

  &--mobile {
    height: 32px;
    padding: 8px 36px 8px 12px;
    transition:
      height $mobile-expand-duration $mobile-expand-ease,
      padding $mobile-expand-duration $mobile-expand-ease;

    &.is-mobile-expanded {
      height: 35px;
    }

    &::placeholder {
      font-size: 0.75rem;
      line-height: 16px;
      opacity: 1;
      color: #303030;
      font-weight: 400;
    }

    &::-webkit-input-placeholder {
      font-size: 0.75rem;
      line-height: 16px;
      opacity: 1;
      color: #303030;
      font-weight: 400;
    }

    &::-moz-placeholder {
      font-size: 0.75rem;
      line-height: 16px;
      opacity: 1;
      color: #303030;
      font-weight: 400;
    }
  }
}

.c-overview-header-and-filters-v1__search-wrap .c-overview-header-and-filters-v1__search-input {
  color: #303030;
  font-weight: 400;
}

.c-overview-header-and-filters-v1__search-icon {
  right: 16px;
  transition: right 0.35s cubic-bezier(0.4, 0, 0.2, 1);

  .is-expanded & {
    right: 24px;
  }
}

$height-dropdown-clamp: 8px;

@mixin dropdown-clamp() {
  &:after {
    @apply absolute bg-white;
    content: '';
    top: -4px;
    left: 60px;
    transform: translate(-50%, -50%);
    height: $height-dropdown-clamp;
    width: 17px;
    border-radius: 150px 150px 0 0;
  }
}

.c-filter-dropdown {
  @apply absolute w-full;
  @include dropdown-clamp;
  top: calc(#{$height-dropdown-clamp} + 38px);

  &::after {
    left: 50%;
  }
}

.c-filter-dropdown__content {
  $width-filter-dropdown: 640px;
  $width-filter-dropdown-xl: 768px;
  $width-filter-dropdown-2xl: 900px;
  top: calc(48px + #{$height-dropdown-clamp});
  box-shadow: 0px -8px 20px rgba(0, 0, 0, 0.05);
  width: $width-filter-dropdown;

  @screen lg {
    max-height: 60vh;
  }

  @screen xl {
    width: $width-filter-dropdown-xl;
  }

  @screen 2xl {
    width: $width-filter-dropdown-2xl;
  }
}

.s-datepicker {
  // Custom override for V-Calendar styling
  .vc-popover-content {
    box-shadow: 0 0 20px rgba(0, 0, 0, 0.05);
    z-index: 30;
    top: 6px;
    @include dropdown-clamp;
    @apply px-3 rounded-tl-none rounded-tr-none border-none;
  }

  .vc-container {
    @apply rounded-br-3xl rounded-bl-3xl;
  }

  .vc-popover-caret {
    display: none;
  }
}

.c-search-icon {
  @apply w-3.5 h-3.5 absolute text-black right-0 top-0 mt-1.5 mr-2 shrink-0;

  @screen lg {
    @apply w-4 h-4 text-app-primary-1-red--fire-brick mt-2 mr-6;
  }
}

.c-search-placeholder {
  &::placeholder {
    @apply opacity-100 italic text-black;

    @screen md {
      @apply font-semibold text-xs;
    }

    @screen lg {
      @apply font-semibold text-sm;
    }
  }
}
</style>
